import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type MessageKey, useLocale, useT } from "@trust/i18n";
import { Spin } from "antd";
import { type GuideListItem, listGuides } from "@/features/guides/api";

/**
 * 标准排名页(任务 P0.5)— 复刻旧站 fastj/client/src/views/inx/portal/rank/index.vue。
 * 页面底 bg-portal-bg 由 PortalLayout 提供,本页只渲染白卡 + TOP100 表格。
 * 数据:GET /api/v1/guides?status=Scored&status=Leveled&sort=score&order=desc&page_size=100
 * —— 新状态机把旧站 Scored 拆为 Scored(评分完成)→ Leveled(终审定级),
 * 两者均属旧站"已评分"语义,故一并查询(否则已定级指南会从榜单消失)。
 */

/** 旧站内嵌的标题中英对照(属数据翻译:DB 有 title_en 时优先,本表仅作兜底) */
const TITLE_ZH_TO_EN: Record<string, string> = {
  认知症服务循证标准: "Evidence-Based Standards for Dementia Care Services",
  青少年反社会行为管理循证指南:
    "Evidence-Based Guidelines for Managing Adolescent Antisocial Behavior",
  出院准备服务循证指南: "Evidence-Based Guidelines for Discharge Planning Services",
  慢性癌性疼痛专家共识: "Expert Consensus on Chronic Cancer Pain",
  肿瘤患者整合照护循证指南: "Evidence-Based Guidelines for Integrated Care in Cancer Patients",
  心血管全过程管理: "Comprehensive Cardiovascular Care Management",
  骨质疏松全程管理循证指南: "Evidence-Based Guidelines for Comprehensive Osteoporosis Management",
};

/** DB 学科分类值(中文枚举)→ subject.* i18n key */
const SUBJECT_KEYS: Record<string, MessageKey> = {
  "社会科学-社会工作": "subject.socialWork",
  "社会科学-社会政策": "subject.socialPolicy",
  "社会科学-心理学": "subject.psychology",
  "社会科学-教育学": "subject.education",
  "社会科学-管理学": "subject.management",
  "社会科学-经济学": "subject.economics",
  "社会科学-其他": "subject.socialOther",
  "健康与医学-补充与替代医学": "subject.complementaryMedicine",
  "健康与医学-公共卫生": "subject.publicHealth",
  "健康与医学-临床医学": "subject.clinicalMedicine",
  "健康与医学-护理学": "subject.nursing",
  "健康与医学-康复医学": "subject.rehabilitationMedicine",
  "健康与医学-整合护理": "subject.integrativeCare",
};

export function RankPage() {
  const t = useT();
  const locale = useLocale();
  const q = useQuery({
    queryKey: ["rank-top100"],
    queryFn: () =>
      listGuides({
        page: 1,
        page_size: 100,
        sort: "score",
        order: "desc",
        status: ["Scored", "Leveled"],
        // 对齐旧站排名口径:只展示公开评分的指南
        show_score: true,
      }),
  });
  const list = q.data?.items ?? [];

  const titleOf = (g: GuideListItem) =>
    locale === "en" ? (g.title_en ?? TITLE_ZH_TO_EN[g.title] ?? g.title) : g.title;
  const subjectOf = (g: GuideListItem) => {
    const key = g.subject_classification ? SUBJECT_KEYS[g.subject_classification] : undefined;
    return key ? t(key) : (g.subject_classification ?? "");
  };

  return (
    <div className="w-full">
      {/* 白卡 */}
      <div className="mt-[20px] min-h-[700px] rounded-[20px] bg-white p-[20px]">
        <div className="flex items-center justify-center font-semibold text-[22px] text-portal-navy">
          {t("inx.rank.top100")}
        </div>

        <Spin spinning={q.isLoading}>
          {/* 表格容器:横向滚动,内层 min-w-1200px */}
          <div className="mt-[20px] box-border shrink-0 overflow-x-scroll bg-portal-table-bg">
            <div className="min-w-[1200px]">
              {/* 表头 */}
              <div className="flex h-[50px] w-full bg-portal-navy text-white">
                <div className="flex h-[50px] w-[80px] shrink-0 items-center justify-center">
                  {t("inx.rank.field1")}
                </div>
                <div className="flex h-[50px] min-w-[380px] flex-1 shrink-0 items-center justify-center">
                  {t("inx.rank.field2")}
                </div>
                <div className="flex h-[50px] w-[160px] shrink-0 items-center justify-center">
                  {t("inx.rank.field3")}
                </div>
                <div className="flex h-[50px] w-[200px] shrink-0 items-center justify-center">
                  {t("inx.rank.field4")}
                </div>
                <div className="flex h-[50px] w-[120px] shrink-0 items-center justify-center">
                  {t("inx.rank.field5")}
                </div>
              </div>
              {/* 数据行 */}
              {list.map((g, idx) => (
                <div
                  key={g.id}
                  className="flex h-[50px] w-full break-all text-[#333333] text-[14px]"
                >
                  <div className="flex h-[50px] w-[80px] shrink-0 items-center justify-center">
                    {idx + 1}
                  </div>
                  <div className="box-border flex h-[50px] min-w-[380px] flex-1 shrink-0 items-center justify-start pr-[20px]">
                    {/* 旧站为默认 el-link:#606266,hover 变 #409EFF + 下划线 */}
                    <Link
                      to="/inx/guide/$guideId"
                      params={{ guideId: String(g.id) }}
                      className="text-[#606266] no-underline hover:text-portal-primary hover:underline"
                    >
                      {titleOf(g)}
                    </Link>
                  </div>
                  <div className="flex h-[50px] w-[160px] shrink-0 items-center pr-[10px]">
                    {subjectOf(g)}
                  </div>
                  <div className="h-[50px] w-[200px] shrink-0 truncate pr-[10px] text-center leading-[50px]">
                    {g.register}
                  </div>
                  <div className="flex h-[50px] w-[120px] shrink-0 items-center justify-center">
                    {g.level}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
}
